/**
 * Product Discount Cards Component
 * Handles copy to clipboard functionality and scroll navigation
 */

class ProductDiscountCards extends HTMLElement {
  constructor() {
    super();
    this.container = null;
    this.prevBtn = null;
    this.nextBtn = null;
    this.init();
  }

  init() {
    this.container = this.querySelector('.discount-cards-container');
    this.prevBtn = this.querySelector('.discount-cards-scroll-btn--prev');
    this.nextBtn = this.querySelector('.discount-cards-scroll-btn--next');
    
    // Copy functionality
    const copyButtons = this.querySelectorAll('.discount-card__copy-btn');
    copyButtons.forEach(button => {
      button.addEventListener('click', this.handleCopy.bind(this));
    });

    // Scroll functionality
    if (this.container && this.prevBtn && this.nextBtn) {
      this.setupScrollButtons();
      
      // Check on load and resize
      this.checkScrollNeeded();
      this.updateScrollButtons();
      
      this.container.addEventListener('scroll', () => {
        this.updateScrollButtons();
      });
      
      // Check on window resize
      let resizeTimeout;
      window.addEventListener('resize', () => {
        clearTimeout(resizeTimeout);
        resizeTimeout = setTimeout(() => {
          this.checkScrollNeeded();
          this.updateScrollButtons();
        }, 100);
      });
    }
  }

  checkScrollNeeded() {
    if (!this.container) return;
    
    const { scrollWidth, clientWidth } = this.container;
    const needsScroll = scrollWidth > clientWidth;
    
    if (needsScroll) {
      this.prevBtn.classList.add('visible');
      this.nextBtn.classList.add('visible');
    } else {
      this.prevBtn.classList.remove('visible');
      this.nextBtn.classList.remove('visible');
      this.prevBtn.classList.remove('disabled');
      this.nextBtn.classList.remove('disabled');
    }
  }

  setupScrollButtons() {
    const scrollAmount = 220; // Slightly more than card width + gap
    
    this.prevBtn.addEventListener('click', () => {
      this.container.scrollBy({
        left: -scrollAmount,
        behavior: 'smooth'
      });
    });

    this.nextBtn.addEventListener('click', () => {
      this.container.scrollBy({
        left: scrollAmount,
        behavior: 'smooth'
      });
    });
  }

  updateScrollButtons() {
    if (!this.container) return;
    
    const { scrollLeft, scrollWidth, clientWidth } = this.container;
    const needsScroll = scrollWidth > clientWidth;
    
    if (!needsScroll) {
      this.prevBtn.classList.remove('visible');
      this.nextBtn.classList.remove('visible');
      return;
    }
    
    // Show buttons if scrolling is needed
    this.prevBtn.classList.add('visible');
    this.nextBtn.classList.add('visible');
    
    // Hide prev button if at start
    if (scrollLeft <= 10) {
      this.prevBtn.classList.add('disabled');
    } else {
      this.prevBtn.classList.remove('disabled');
    }
    
    // Hide next button if at end
    if (scrollLeft >= scrollWidth - clientWidth - 10) {
      this.nextBtn.classList.add('disabled');
    } else {
      this.nextBtn.classList.remove('disabled');
    }
  }

  handleCopy(event) {
    const button = event.currentTarget;
    const code = button.getAttribute('data-copy-code');
    
    if (!code) return;

    // Copy to clipboard
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(code).then(() => {
        this.showCopiedState(button);
      }).catch(err => {
        console.error('Failed to copy:', err);
        this.fallbackCopy(code, button);
      });
    } else {
      // Fallback for older browsers
      this.fallbackCopy(code, button);
    }
  }

  fallbackCopy(text, button) {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    textArea.style.top = '-999999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    
    try {
      document.execCommand('copy');
      this.showCopiedState(button);
    } catch (err) {
      console.error('Fallback copy failed:', err);
    } finally {
      document.body.removeChild(textArea);
    }
  }

  showCopiedState(button) {
    // Add copied class
    button.classList.add('copied');
    
    // Reset after 2 seconds
    setTimeout(() => {
      button.classList.remove('copied');
    }, 2000);
    
    // Add visual feedback
    const card = button.closest('.discount-card');
    if (card) {
      card.style.transform = 'scale(1.02)';
      setTimeout(() => {
        card.style.transform = '';
      }, 200);
    }
  }
}

customElements.define('product-discount-cards', ProductDiscountCards);

// Initialize for existing elements
document.addEventListener('DOMContentLoaded', () => {
  const discountCardsContainers = document.querySelectorAll('.product-discount-cards');
  discountCardsContainers.forEach(container => {
    if (!container.closest('product-discount-cards')) {
      const wrapper = document.createElement('product-discount-cards');
      container.parentNode.insertBefore(wrapper, container);
      wrapper.appendChild(container);
    }
  });
});
